import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Twitter } from 'lucide-react';

const TermsOfService = () => (
  <div className="min-h-screen bg-[#020817] text-white antialiased">
    {/* Nav */}
    <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-[#020817]/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Brownlow Genius home">
          <img src="/favicon.svg" alt="" className="h-7 w-7 object-contain" />
          <span className="text-[15px] font-semibold tracking-tight text-white">Brownlow Genius</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-slate-300 transition-colors hover:bg-white/[0.04] hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
          Back to home
        </Link>
      </div>
    </header>

    {/* Header */}
    <div className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/[0.08] blur-[160px]" />
      <div className="relative mx-auto max-w-6xl px-5 pb-6 pt-16 text-center sm:px-6 md:pt-24">
        <h1 className="text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
          <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
            Terms of Service
          </span>
        </h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: 13 September 2026</p>
      </div>
    </div>

    {/* Content */}
    <div className="mx-auto max-w-3xl px-5 pb-20 pt-8 sm:px-6 md:pb-28">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7 md:p-10">
        <div className="space-y-10 text-[15px] leading-relaxed text-slate-400">
          <section>
            <p>
              These Terms of Service ("Terms") govern your access to and use of the Brownlow Genius platform (the "Service"), which is owned and operated by SnipeAIO Pty Ltd ("SnipeAIO", "we", "us", or "our").
            </p>
            <p className="mt-4 font-medium text-slate-200">
              By creating an account, accessing, or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">1. Free Access and Accounts</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-200">1.1 Free Service</h3>
                <div className="mt-1 space-y-3">
                  <p>The Service is currently provided free of charge to registered users. You must create an account to access and use the Service.</p>
                  <p>No payment is currently required to create an account or access the Service.</p>
                  <p>We reserve the right to introduce paid features, subscriptions, or other paid products in the future. You will not be charged unless you expressly agree to purchase or subscribe to a paid product or service.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.2 Account Registration</h3>
                <div className="mt-1 space-y-3">
                  <p>To use the Service, you may be required to provide information including your email address, password, or other account information.</p>
                  <p>You agree to provide accurate and current information when creating and maintaining your account.</p>
                  <p>You are responsible for maintaining the confidentiality and security of your account credentials and for activity occurring through your account.</p>
                  <p>You must notify us if you become aware of any unauthorised access to or use of your account.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.3 Account Suspension and Termination</h3>
                <div className="mt-1 space-y-3">
                  <p>We reserve the right to suspend, restrict, or terminate your account or access to the Service where we reasonably believe that:</p>
                  <ul className="list-disc space-y-2 pl-6">
                    <li>you have breached these Terms;</li>
                    <li>your account is being used fraudulently or unlawfully;</li>
                    <li>your use of the Service may harm SnipeAIO, the Service, or other users;</li>
                    <li>you have attempted to interfere with, misuse, scrape, reverse engineer, or gain unauthorised access to the Service; or</li>
                    <li>suspension or termination is otherwise reasonably necessary to protect the security, integrity, or operation of the Service.</li>
                  </ul>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">1.4 Availability</h3>
                <div className="mt-1 space-y-3">
                  <p>We strive to maintain continuous availability of the Service but do not guarantee that the Service will always be available, uninterrupted, secure, or error-free.</p>
                  <p>The Service may be temporarily unavailable due to maintenance, updates, technical issues, third-party services, or circumstances outside our reasonable control.</p>
                  <p>We may modify, suspend, discontinue, or replace any part of the Service at any time, subject to applicable law.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">2. Communications and Marketing</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-200">2.1 Service Communications</h3>
                <div className="mt-1 space-y-3">
                  <p>By creating an account, you acknowledge that we may send you communications reasonably necessary to provide, administer, secure, or maintain your account and the Service.</p>
                  <p>These communications may include account verification emails, password resets, security notifications, important Service announcements, changes to these Terms or our Privacy Policy, and other administrative communications.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">2.2 Marketing Communications</h3>
                <div className="mt-1 space-y-3">
                  <p>Where you have provided your consent, we may use your email address to send you marketing communications relating to Brownlow Genius.</p>
                  <p>These communications may include information about new Brownlow Genius products, product launches, features, updates, promotions, or offers that we believe may be relevant to you.</p>
                  <p>
                    You may withdraw your consent to receive marketing communications at any time by using the unsubscribe link provided in our marketing emails, or by{' '}
                    <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">contacting us here</a>.
                  </p>
                  <p>Unsubscribing from marketing communications will not prevent you from receiving communications that are reasonably necessary for the operation, administration, or security of your account or the Service.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">2.3 Privacy</h3>
                <p className="mt-1">
                  Our collection, use, disclosure, storage, and handling of your personal information, including your email address and account information, is governed by our{' '}
                  <Link to="/privacy" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">3. Ownership and Licence</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-200">3.1 Ownership</h3>
                <div className="mt-1 space-y-3">
                  <p>All intellectual property rights in the Service, including its software, source code, design, branding, database structure, models, calculations, projections, visualisations, graphics, and other content we create, are owned by or licensed to SnipeAIO.</p>
                  <p>Some information made available through the Service, including certain vote counts, is sourced from or provided by third parties. Those third parties retain all intellectual property rights in their own data and content, and we make that information available through the Service under licence or as otherwise permitted.</p>
                  <p>The selection, compilation, arrangement, aggregation, and presentation of data within the Service, including our own vote counts and any consensus or aggregate outputs derived in whole or in part from third-party data, are owned by or licensed to SnipeAIO.</p>
                  <p>Nothing in these Terms transfers any intellectual property rights to you, whether in our content or in third-party content made available through the Service.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">3.2 Licence</h3>
                <div className="mt-1 space-y-3">
                  <p>Subject to these Terms, we grant you a limited, revocable, non-exclusive, non-transferable and non-sublicensable licence to access and use the Service for your personal, non-commercial purposes.</p>
                  <p>You may not reproduce, republish, distribute, sell, commercially exploit, modify, create derivative works from, or otherwise use content from the Service except as expressly permitted by us or by applicable law.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">3.3 Prohibited Use</h3>
                <div className="mt-1 space-y-3">
                  <p>You must not:</p>
                  <ul className="list-disc space-y-2 pl-6">
                    <li>use the Service for any unlawful purpose;</li>
                    <li>attempt to gain unauthorised access to the Service, another user&apos;s account, or any systems or networks associated with the Service;</li>
                    <li>interfere with or disrupt the operation, security, or integrity of the Service;</li>
                    <li>use automated systems, bots, scripts, crawlers, scrapers, or similar technologies to systematically extract or collect data from the Service without our prior written permission;</li>
                    <li>reverse engineer, decompile, disassemble, or attempt to derive the source code or underlying technology of the Service except where permitted by law;</li>
                    <li>reproduce or commercially exploit our or our licensors&apos; data, projections, models, visualisations, or other content without our prior written permission;</li>
                    <li>impersonate another person or provide false or misleading account information; or</li>
                    <li>use the Service in a manner that could damage, disable, overburden, or impair the Service.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">4. Data Accuracy and Disclaimer</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-200">4.1 Accuracy of Data</h3>
                <div className="mt-1 space-y-3">
                  <p>While we make reasonable efforts to provide accurate and up-to-date information, we do not guarantee the accuracy, completeness, reliability, availability, or timeliness of any information displayed through the Service.</p>
                  <p>Vote counts, predictions, projections, probabilities, rankings, statistics, and other content may be based on internal processes, statistical models, estimates, publicly available information, or third-party sources and may contain errors or inaccuracies.</p>
                  <p>Information displayed through the Service may change at any time.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">4.2 Predictions and Projections</h3>
                <div className="mt-1 space-y-3">
                  <p>Any predictions, projected vote totals, rankings, probabilities, statistical outputs, or similar information displayed through Brownlow Genius are estimates only.</p>
                  <p>Past performance, historical accuracy, or previous results do not guarantee future results.</p>
                  <p>Actual Brownlow Medal voting and other sporting outcomes may differ materially from the information, predictions, or projections displayed through the Service.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">4.3 No Financial or Betting Advice</h3>
                <div className="mt-1 space-y-3">
                  <p>The Service is provided for informational and analytical purposes only.</p>
                  <p>Nothing provided through the Service constitutes financial, betting, gambling, investment, or other professional advice, nor does anything provided through the Service constitute a recommendation or guarantee regarding any particular betting outcome.</p>
                  <p>You are solely responsible for any decisions you make based on information obtained through the Service.</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-200">4.4 Betting Risk</h3>
                <div className="mt-1 space-y-3">
                  <p>You acknowledge that betting and gambling involve financial risk and that you may lose some or all of the money you choose to wager.</p>
                  <p>You are responsible for independently assessing and verifying any information before making a betting decision.</p>
                  <p>SnipeAIO does not operate a bookmaker, accept wagers, or guarantee that any betting opportunity, prediction, projection, or other information displayed through the Service will result in a successful outcome.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">5. Third-Party Information and Services</h2>
            <div className="space-y-3">
              <p>The Service may contain information obtained from or relating to third parties, including sporting data, betting markets, odds, statistics, websites, platforms, or other external sources.</p>
              <p>We do not control and are not responsible for the availability, accuracy, content, practices, or services of third parties.</p>
              <p>References or links to third-party services do not constitute an endorsement of those services.</p>
              <p>Your use of any third-party website, bookmaker, platform, or service is subject to that third party&apos;s own terms, conditions, and policies.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">6. Limitation of Liability</h2>
            <div className="space-y-3">
              <p>To the maximum extent permitted by law, SnipeAIO and its directors, officers, employees, contractors, and affiliates will not be liable for any indirect, incidental, special, consequential, or similar loss or damage arising from or relating to your access to or use of the Service.</p>
              <p>This includes, without limitation, loss of profits, loss of revenue, loss of data, loss of opportunity, betting losses, or losses resulting from reliance on information, predictions, projections, probabilities, statistics, or other content made available through the Service.</p>
              <p>The Service is currently provided free of charge and on an "as available" basis. To the maximum extent permitted by law, we make no warranties or representations regarding the availability, accuracy, reliability, suitability, or performance of the Service.</p>
              <p>Nothing in these Terms excludes, restricts, or modifies any liability, consumer guarantee, right, or remedy that cannot lawfully be excluded, restricted, or modified.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">7. Age Restriction</h2>
            <div className="space-y-3">
              <p>The Service is intended only for individuals aged 18 years or older.</p>
              <p>By creating an account or using the Service, you represent that you are at least 18 years old.</p>
              <p>If we reasonably believe that you are under 18, we may suspend or terminate your account and access to the Service.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">8. Responsible Gambling</h2>
            <div className="space-y-3">
              <p>Brownlow Genius provides sporting information and analytics and does not accept or facilitate wagers.</p>
              <p>If you choose to participate in betting or gambling activities, you should do so responsibly and only wager amounts you can afford to lose.</p>
              <p>If gambling is causing you or someone you know harm, you should consider seeking assistance from an appropriate responsible gambling support service.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">9. Privacy</h2>
            <div className="space-y-3">
              <p>
                Your use of the Service is also governed by our{' '}
                <Link to="/privacy" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">Privacy Policy</Link>
                , which explains how we collect, use, disclose, store, and protect your personal information.
              </p>
              <p>By creating an account and using the Service, you acknowledge that you have had the opportunity to review our Privacy Policy.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">10. Changes to the Service</h2>
            <div className="space-y-3">
              <p>We may modify, update, add to, restrict, suspend, or discontinue features or functionality of the Service from time to time.</p>
              <p>We may also introduce new products, services, or paid features in the future.</p>
              <p>Where a separate payment, subscription, or additional terms apply to a new product or feature, those terms will be presented to you before you purchase or subscribe to that product or feature.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">11. Amendments to These Terms</h2>
            <div className="space-y-3">
              <p>We may update these Terms from time to time.</p>
              <p>Where changes are material, we may provide reasonable notice through the Service, by email, or by other appropriate means.</p>
              <p>The updated Terms will take effect from the date specified when they are published or otherwise communicated to you.</p>
              <p>Your continued use of the Service after the updated Terms take effect constitutes your acceptance of the revised Terms.</p>
              <p>If you do not agree to the revised Terms, you must stop using the Service.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">12. Consumer Law Rights (Australia)</h2>
            <div className="space-y-3">
              <p>Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, remedy, or other protection provided by the Australian Consumer Law or any other applicable law that cannot lawfully be excluded, restricted, or modified.</p>
              <p>Where permitted by law and where our liability may be limited, our liability will be limited to the extent permitted under applicable law.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">13. Governing Law</h2>
            <div className="space-y-3">
              <p>These Terms are governed by the laws of Victoria, Australia.</p>
              <p>You submit to the jurisdiction of the courts of Victoria and any courts entitled to hear appeals from those courts.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-white md:text-2xl">14. Contact</h2>
            <p>
              If you have any questions regarding these Terms, your account, or the Service, you can{' '}
              <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">contact us here</a>
              {' '}or email SnipeAIO Pty Ltd directly at{' '}
              <a href="mailto:info@snipeaio.com" className="font-medium text-emerald-400 transition-colors hover:text-emerald-300">info@snipeaio.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer className="border-t border-white/[0.05]">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <div className="flex flex-col items-center gap-6 py-10 text-center md:flex-row md:items-center md:justify-between md:text-left">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="" className="h-6 w-6 object-contain" />
            <span className="text-sm font-semibold tracking-tight text-white">Brownlow Genius</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] md:justify-end">
            <Link to="/terms" className="text-slate-400 transition-colors hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-400 transition-colors hover:text-white">Privacy Policy</Link>
            <a href="https://x.com/brownlow_genius" target="_blank" rel="noopener noreferrer" aria-label="Follow Brownlow Genius on X" className="text-slate-400 transition-colors hover:text-white">
              <Twitter className="h-4 w-4" aria-hidden="true" />
            </a>
            <span className="text-slate-600">© Brownlow Genius</span>
          </div>
        </div>
        <div className="border-t border-white/[0.05] py-5">
          <p className="text-center text-[13px] leading-relaxed text-slate-500">
            Always remember to gamble responsibly. For free and confidential support call{' '}
            <a href="tel:1800858858" className="font-medium text-slate-400 transition-colors hover:text-white">1800 858 858</a>
            {' '}or visit{' '}
            <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-400 transition-colors hover:text-white">gamblinghelponline.org.au</a>.
          </p>
        </div>
      </div>
    </footer>
  </div>
);

export default TermsOfService;
